import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Carousel from '@nerdwallet/react-carousel';
import classNames from 'classnames';

import { CenteredContainer, Box, Button, Title } from '@nerdwallet/currency';
import Right from '@nerdwallet/react-icon/gizmo/Right';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _ from 'lodash/';

import {
  repeatable,
  boolCheckboxSpec,
  stringSelectSpec,
  stringInputSpec,
} from '../../lib/editing-configs';
import getTextColor from '../../lib/text-color-config';

import BackgroundColor from '../background-color/background-color';
import parseStyleFormatting from '../../lib/parse-style-formatting';

import FlexCardItem from './flex-card-item/flex-card-item';
import {
  CARDS_PER_ROW_ENUM,
  CARD_TREATMENT_ENUM,
  ALIGNMENT_ENUM,
} from './lib/constants';

import './flex-card.less';

const setRowStyle = (cardsPerRow, cards, size) => {
  const total = Math.ceil(cards.length / parseInt(cardsPerRow, 10));
  if (size === 'md' && cardsPerRow === '4') {
    return {
      gridTemplateColumns: 'repeat(3, 1fr)',
      gridTemplateRows: `repeat(${Math.ceil(cards.length / 3)}, 1fr)`,
    };
  }
  if (size === 'sm') {
    return {
      gridTemplateColumns: `repeat(1, 1fr)`,
      gridTemplateRows: `repeat(${cards.length}, 1fr)`,
    };
  }
  return {
    gridTemplateColumns: `repeat(${cardsPerRow}, 1fr)`,
    gridTemplateRows: `repeat(${total}, 1fr)`,
  };
};

const titleSizeEnum = {
  'x-large': 'default',
  large: 'small',
};

const NextSlide = ({ onClick, arrowColor, className }) => {
  const classStyle = className.replace('slick-arrow slick-next', '');

  return (
    <Button
      className={classNames([classStyle, 'flex-card-list-carousel-arrow'])}
      tertiary
      onClick={onClick}
    >
      <Right size={30} color={arrowColor} />
    </Button>
  );
};

const FlexCard = ({
  arrowColor,
  stacked,
  size,
  cardsPerRow,
  cardTreatment,
  borderColorTreatment,
  borderSizeTreatment,
  desktopCarousel,
  cardsToPreview,
  shadowColorTreatment,
  title,
  titleInChronicle,
  titleAlignment,
  bgOptions,
  cards,
  titleSize,
  wideSizeLayout,
  textColorOptions,
  carouselDotColorOptions,
  carouselInactiveDotColorOptions,
  carouselArrowColorOptions,
  carouselArrowBackgroundOptions,
  displayMobileCarouselArrow,
  arrowsOnTheEdges,
}) => {
  const { textColor: carouselDotColor } = getTextColor(carouselDotColorOptions);
  const { textColor: carouselInactiveDotColor } = getTextColor(
    carouselInactiveDotColorOptions
  );
  const { textColor: carouselArrowColor } = getTextColor(
    carouselArrowColorOptions
  );
  const { textColor: carouselArrowBackgroundColor } = getTextColor(
    carouselArrowBackgroundOptions
  );
  const isMobileCarousel = size === 'sm' && !stacked;
  const isDesktopCarousel = desktopCarousel && size !== 'sm';
  const flexCardContainerRef = useRef();

  useEffect(() => {
    if (flexCardContainerRef.current) {
      const dotList =
        flexCardContainerRef.current.querySelector('ul.slick-dots');

      if (dotList && carouselDotColor) {
        dotList.classList.add('custom-dot-color');
        dotList.style.setProperty('--activeDot', carouselInactiveDotColor);
        dotList
          .querySelectorAll('.flex-card-list-dots li button')
          .forEach((button) => {
            // eslint-disable-next-line no-param-reassign
            button.style.color = carouselDotColor;
          });
      }

      const slickArrows =
        flexCardContainerRef.current.querySelectorAll('button.slick-arrow');

      if (slickArrows.length) {
        slickArrows.forEach((arrow) => {
          if (carouselArrowBackgroundColor) {
            // eslint-disable-next-line no-param-reassign
            arrow.style.backgroundColor = carouselArrowBackgroundColor;
          }
          if (carouselArrowColor) {
            // eslint-disable-next-line no-param-reassign
            arrow.style.color = carouselArrowColor;
          }
        });
      }
    }
  }, [
    bgOptions.backgroundColor,
    carouselArrowBackgroundColor,
    carouselArrowColor,
    carouselDotColor,
    carouselInactiveDotColor,
    displayMobileCarouselArrow,
    isDesktopCarousel,
  ]);

  return (
    <Box ref={flexCardContainerRef}>
      <BackgroundColor bgOptions={bgOptions}>
        {(backgroundColor) => {
          const { textColor } = getTextColor(textColorOptions);
          const colorStyle = { color: textColor };
          const rowStyle = setRowStyle(cardsPerRow, cards, size);
          const titleAlignmentValue =
            size !== 'sm' ? titleAlignment : ALIGNMENT_ENUM.LEFT;
          const mobileStackedOrWide =
            (size !== 'sm' && !desktopCarousel) || (size === 'sm' && stacked);

          return (
            <div style={{ backgroundColor }}>
              <CenteredContainer
                className={classNames([
                  'flex-card-wrapper',
                  {
                    carousel: (size === 'sm' && !stacked) || desktopCarousel,
                    'oversize-layout': !wideSizeLayout || isDesktopCarousel,
                    'no-arrow': !displayMobileCarouselArrow,
                    'arrows-on-the-edges': arrowsOnTheEdges,
                  },
                ])}
              >
                {title && (
                  <Box
                    display="flex"
                    paddingBottom={title ? 4 : 0}
                    justifyContent="space-between"
                    width="100"
                  >
                    <Title
                      size={titleSizeEnum[titleSize]}
                      style={colorStyle}
                      align={titleAlignmentValue}
                      className={classNames('flex-card-wrapper-title', {
                        chronicle: titleInChronicle,
                      })}
                    >
                      {parseStyleFormatting(title)}
                    </Title>
                  </Box>
                )}
                {mobileStackedOrWide && (
                  <Box style={rowStyle} className="flex-card-list-container">
                    {cards.map((item) => (
                      <FlexCardItem
                        key={item.$unique_key}
                        cardTreatment={cardTreatment}
                        borderColorTreatment={borderColorTreatment}
                        borderSizeTreatment={borderSizeTreatment}
                        shadowColorTreatment={shadowColorTreatment}
                        cardsPerRow={cardsPerRow}
                        size={size}
                        textColor={colorStyle}
                        {...item}
                      />
                    ))}
                  </Box>
                )}
                {isMobileCarousel && (
                  <Carousel
                    id="nerd-carousel"
                    autoplay={false}
                    allowLooping
                    decorateNav={false}
                    enableDotNavigation
                    dots
                    nextArrow={
                      displayMobileCarouselArrow ? (
                        <NextSlide arrowColor={arrowColor} />
                      ) : null
                    }
                    dotsClass="slick-dots flex-card-list-dots"
                    slidesToShow="1.15"
                    className="flex-card-list-container"
                  >
                    {cards.map((item) => (
                      <FlexCardItem
                        key={item.$unique_key}
                        cardTreatment={cardTreatment}
                        borderColorTreatment={borderColorTreatment}
                        borderSizeTreatment={borderSizeTreatment}
                        shadowColorTreatment={shadowColorTreatment}
                        cardsPerRow={cardsPerRow}
                        size={size}
                        textColor={colorStyle}
                        {...item}
                      />
                    ))}
                  </Carousel>
                )}
                {isDesktopCarousel && (
                  <Carousel
                    id="nerd-carousel"
                    autoplay={false}
                    allowLooping
                    decorateNav={false}
                    enableDotNavigation
                    dots
                    dotsClass="slick-dots flex-card-list-dots"
                    slidesToShow={cardsToPreview || '3'}
                    className="flex-card-list-container"
                  >
                    {cards.map((item) => (
                      <FlexCardItem
                        key={item.$unique_key}
                        cardTreatment={cardTreatment}
                        desktopCarousel={desktopCarousel}
                        borderColorTreatment={borderColorTreatment}
                        borderSizeTreatment={borderSizeTreatment}
                        shadowColorTreatment={shadowColorTreatment}
                        cardsPerRow={cardsToPreview}
                        size={size}
                        textColor={colorStyle}
                        {...item}
                      />
                    ))}
                  </Carousel>
                )}
              </CenteredContainer>
            </div>
          );
        }}
      </BackgroundColor>
    </Box>
  );
};

FlexCard.getEditingConfig = (componentDocument) => {
  const treatmentShadowOptions =
    _.get(componentDocument, 'props.cardTreatment', '') ===
    CARD_TREATMENT_ENUM.BOX_SHADOW
      ? [
          stringInputSpec(
            'shadowColorTreatment',
            'Card Drop Shadow Color Hex (eg: "#fff" - default is #cdcdcd)'
          ),
        ]
      : '';
  const treatmentBorderOptions =
    _.get(componentDocument, 'props.cardTreatment', '') ===
    CARD_TREATMENT_ENUM.BORDER
      ? [
          stringInputSpec(
            'borderColorTreatment',
            'Card Border Color Hex (eg: "#fff" - default is #cdcdcd)'
          ),
          stringInputSpec(
            'borderSizeTreatment',
            'Card Border Width (in px, eg: "2px" - default is 1px)'
          ),
        ]
      : '';
  const cardsAmountConfig = componentDocument?.props?.desktopCarousel
    ? [
        stringSelectSpec(
          'cardsToPreview',
          'Cards to preview?',
          [
            { label: 'Select an option', value: '' },
            { label: '4', value: CARDS_PER_ROW_ENUM.FOUR },
            { label: '3', value: CARDS_PER_ROW_ENUM.THREE },
            { label: '2', value: CARDS_PER_ROW_ENUM.TWO },
            { label: '1', value: CARDS_PER_ROW_ENUM.ONE },
          ],
          {
            help: 'Defines # of cards displayed in desktop carousel preview',
          }
        ),
      ]
    : [
        stringSelectSpec(
          'cardsPerRow',
          'Cards per Row',
          [
            { label: '4', value: CARDS_PER_ROW_ENUM.FOUR },
            { label: '3', value: CARDS_PER_ROW_ENUM.THREE },
            { label: '2', value: CARDS_PER_ROW_ENUM.TWO },
            { label: '1', value: CARDS_PER_ROW_ENUM.ONE },
          ],
          {
            help: '4 colums layout is only available in desktop, on tablet defaults to 3 colums maximum',
          }
        ),
        boolCheckboxSpec(
          'wideSizeLayout',
          'Display cards in a wider layout? (desktop/tablet only)'
        ),
      ];

  const mobileArrowColorInput = componentDocument?.props
    ?.displayMobileCarouselArrow
    ? [
        stringInputSpec(
          'arrowColor',
          'Mobile Carousel Arrow Color (eg: "#fff" - default is #008255)'
        ),
      ]
    : [];

  return [
    stringInputSpec('title', 'Cards List Title', {
      help: 'For GothamBlack font, surround title text with | like |this|',
    }),
    stringSelectSpec('titleSize', 'Cards List Title Size', [
      { label: 'Default', value: 'x-large' },
      { label: 'Small', value: 'large' },
    ]),
    boolCheckboxSpec('titleInChronicle', 'Title in Chronicle?'),
    stringSelectSpec(
      'titleAlignment',
      'Select Title Alignment',
      [
        { label: 'Left', value: ALIGNMENT_ENUM.LEFT },
        { label: 'Center', value: ALIGNMENT_ENUM.CENTER },
        { label: 'Right', value: ALIGNMENT_ENUM.RIGHT },
      ],
      {
        help: 'Add a border style for the card',
      }
    ),
    boolCheckboxSpec('desktopCarousel', 'Display cards in carousel? (Desktop)'),
    boolCheckboxSpec(
      'displayMobileCarouselArrow',
      'Display Carousel Arrow in Mobile?'
    ),
    boolCheckboxSpec('arrowsOnTheEdges', 'Arrows on the edges?'),
    ...cardsAmountConfig,
    boolCheckboxSpec('stacked', 'Display cards without carousel? (Mobile)'),
    ...mobileArrowColorInput,
    ...getTextColor.getEditingConfig(componentDocument),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'carouselDotColorOptions',
      'Carousel dot color',
      'Carousel dot hex color override'
    ),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'carouselInactiveDotColorOptions',
      'Carousel inactive dot color',
      'Carousel inactive dot hex color override'
    ),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'carouselArrowBackgroundOptions',
      'Carousel arrow background color',
      'Carousel arrow hex background color override'
    ),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'carouselArrowColorOptions',
      'Carousel arrow color',
      'Carousel arrow hex color override'
    ),
    ...BackgroundColor.getEditingConfig(componentDocument),
    stringSelectSpec(
      'cardTreatment',
      'Select Card Treatment',
      [
        { label: 'None', value: CARD_TREATMENT_ENUM.NONE },
        { label: 'Box Shadow', value: CARD_TREATMENT_ENUM.BOX_SHADOW },
        { label: 'Border', value: CARD_TREATMENT_ENUM.BORDER },
      ],
      {
        help: 'Add a border style for the card',
      }
    ),
    ...treatmentShadowOptions,
    ...treatmentBorderOptions,
    repeatable('cards', 'Card', 'Card', (i) =>
      FlexCardItem.getEditingConfig({
        props: componentDocument.props?.cards[i],
      })
    ),
  ];
};

FlexCard.displayName = 'Hero Video';

export default connect(
  createStructuredSelector({
    size: _.property('mediaQuery'),
  })
)(FlexCard);

NextSlide.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  arrowColor: PropTypes.string,
};

FlexCard.propTypes = {
  cards: PropTypes.array,
  stacked: PropTypes.bool,
  size: PropTypes.string,
  cardsPerRow: PropTypes.string,
  desktopCarousel: PropTypes.bool,
  ...BackgroundColor.propTypes,
  cardTreatment: PropTypes.string,
  borderColorTreatment: PropTypes.string,
  borderSizeTreatment: PropTypes.string,
  shadowColorTreatment: PropTypes.string,
  title: PropTypes.string,
  titleSize: PropTypes.string,
  titleInChronicle: PropTypes.bool,
  titleAlignment: PropTypes.string,
  arrowColor: PropTypes.string,
  wideSizeLayout: PropTypes.bool,
  cardsToPreview: PropTypes.string,
  displayMobileCarouselArrow: PropTypes.bool,
  arrowsOnTheEdges: PropTypes.bool,
  children: PropTypes.node,
};

FlexCard.defaultProps = {
  cards: [],
  cardsPerRow: CARDS_PER_ROW_ENUM.FOUR,
  cardsToPreview: CARDS_PER_ROW_ENUM.THREE,
  stacked: false,
  title: 'Flex Card Title',
  cardTreatment: CARD_TREATMENT_ENUM.NONE,
  shadowColorTreatment: '#cdcdcd',
  ...BackgroundColor.defaultProps,
  borderColorTreatment: '#cdcdcd',
  borderSizeTreatment: '1px',
  titleSize: 'x-large',
  arrowColor: '#008255',
  displayMobileCarouselArrow: false,
  arrowsOnTheEdges: false,
  carouselDotColorOptions: {
    hexColor: '#008255',
  },
  carouselInactiveDotColorOptions: {
    hexColor: '#cdcdcd',
  },
  parseLinks: true,
  children: null,
};
